// api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import eventEmitter from "./eventEmitter";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token as string);
  });
  failedQueue = [];
};

/* ================= REQUEST ================= */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = sessionStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    eventEmitter.emit("showLoading");
    return config;
  },
  (error) => {
    eventEmitter.emit("hideLoading");
    return Promise.reject(error);
  }
);

/* ================= RESPONSE ================= */
api.interceptors.response.use(
  (response) => {
    eventEmitter.emit("hideLoading");
    return response;
  },
  async (error: AxiosError) => {
    eventEmitter.emit("hideLoading");

    const originalRequest: any = error.config;

    if (error.response?.status !== 403 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = sessionStorage.getItem("refreshToken");

      if (!refreshToken) {
        window.location.href = "/login";
        return Promise.reject(error);
      }

      const response = await axios.post(
        `${baseURL}/auth/refresh-token`,
        { refreshToken },
        { withCredentials: true }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      sessionStorage.setItem("authToken", accessToken);
      if (newRefreshToken) {
        sessionStorage.setItem("refreshToken", newRefreshToken);
      }

      processQueue(null, accessToken);

      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (err) {
      processQueue(err, null);
      sessionStorage.clear();
      window.location.href = "/login";
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;